import { formatPacingNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { ChartTooltipV2, PayloadItem } from '@Components/Charts/ChartTooltip/ChartTooltipV2';
import { PacingBarColors } from '@src/utils/colors';

interface PacingTargets {
  budget: number;
  assumedClearanceRate: number;
}

interface PacingOverlayProps {
  title: string;
  reprocessing: boolean;
  logs: number;
  estimated: number;
  booked: number;
  // The delivery split is left out when the row has none to show.
  secured?: number;
  unsecured?: number;
  upfront?: number;
  // Only rows with a TV budget have targets.
  targets?: PacingTargets;
}

const dollars = (value: number | undefined) =>
  value === undefined ? undefined : formatPacingNumber(value, '$');

// Hover breakdown for a pacing bar; keys match the bar's layer and marker colors.
export const PacingOverlay = ({
  title,
  reprocessing,
  logs,
  estimated,
  booked,
  secured,
  unsecured,
  upfront,
  targets
}: PacingOverlayProps) => {
  const items: PayloadItem[] = [
    { label: 'On the logs', value: dollars(logs), color: undefined },
    {
      label: 'Upfront',
      value: dollars(upfront),
      color: PacingBarColors.deliveryUpfront,
      keyShape: 'square'
    },
    {
      label: 'Secured',
      value: dollars(secured),
      color: PacingBarColors.deliverySecured,
      keyShape: 'square'
    },
    {
      label: 'Unsecured',
      value: dollars(unsecured),
      color: PacingBarColors.deliveryUnsecured,
      keyShape: 'square'
    },
    {
      label: 'Pacing',
      value: dollars(estimated),
      color: PacingBarColors.pacing,
      keyShape: 'square'
    },
    { label: 'Booked', value: dollars(booked), color: PacingBarColors.booked },
    ...(targets
      ? [
          {
            label: 'Client Target',
            value: dollars(targets.budget),
            color: PacingBarColors.clientTarget
          },
          {
            label: 'Booked Target',
            value: dollars(booked * targets.assumedClearanceRate),
            color: PacingBarColors.bookedTarget
          },
          {
            label: 'Assumed Clear',
            value: `${(100 * targets.assumedClearanceRate).toFixed(0)}%`,
            color: undefined
          }
        ]
      : [])
  ];

  return (
    <ChartTooltipV2
      headers={[title]}
      subHeaderLabel={reprocessing ? 'Reprocessing' : undefined}
      items={items}
    />
  );
};
